import {
  CheckCircle,
  Calendar,
  Weight,
  Ruler,
  Palette,
  User,
  Tag,
  Heart,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function AnimalViewer2({ animal }: { animal: any }) {
  const getAnimalIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "BOVIN":
        return "🐄";
      case "CAPRIN":
        return "🐐";
      case "OVIN":
        return "🐑";
      case "PORCIN":
        return "🐷";
      case "VOLAILLE":
        return "🐔";
      default:
        return "🐾";
    }
  };

  const getSexeDisplay = (sexe: string) => {
    switch (sexe?.toUpperCase()) {
      case "M":
        return { label: "Mâle", icon: "♂️", color: "text-blue-600" };
      case "F":
        return { label: "Femelle", icon: "♀️", color: "text-pink-600" };
      default:
        return { label: "Non spécifié", icon: "⚪", color: "text-gray-600" };
    }
  };

  const sexeInfo = getSexeDisplay(animal.sexe || "");

  return (
    <div className="p-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Colonne 1: Avatar et infos principales */}
        <div className="lg:col-span-1">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center text-6xl mb-4 shadow-lg">
              {getAnimalIcon(animal.animalType || animal.type || "")}
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {animal.nom || "Animal sans nom"}
            </h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-lg">{sexeInfo.icon}</span>
              <span className={`font-medium ${sexeInfo.color}`}>
                {sexeInfo.label}
              </span>
            </div>
            <div className="bg-gray-100 rounded-lg p-3">
              <p className="text-sm text-gray-600 mb-1">Matricule</p>
              <p className="font-mono font-bold text-lg text-gray-800">
                {animal.matricule || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Colonne 2: Détails de l'animal */}
        <div className="lg:col-span-2">
          <h4 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            Informations de l'animal
          </h4>

          <div className="grid grid-cols-1 gap-6">
            {/* Type et espèce */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Tag className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-blue-600 font-medium">Type</p>
                  <p className="font-semibold text-gray-800">
                    {animal.type || animal.animalType || "Non spécifié"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-5 h-5 text-purple-600 font-bold">🧬</div>
                <div>
                  <p className="text-sm text-purple-600 font-medium">Espèce</p>
                  <p className="font-semibold text-gray-800">
                    {animal.espece || "Non spécifiée"}
                  </p>
                </div>
              </div>
            </div>

            {/* Caractéristiques physiques */}
            <div className="space-y-4">
              {animal.poids !== 0 && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <Weight className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Poids</p>
                    <p className="font-semibold text-gray-800">
                      {animal.poids} kg
                    </p>
                  </div>
                </div>
              )}

              {animal.taille !== 0 && (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Ruler className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-green-600 font-medium">Taille</p>
                    <p className="font-semibold text-gray-800">
                      {animal.taille} cm
                    </p>
                  </div>
                </div>
              )}

              {animal.couleur && (
                <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg">
                  <Palette className="w-5 h-5 text-pink-600" />
                  <div>
                    <p className="text-sm text-pink-600 font-medium">Couleur</p>
                    <p className="font-semibold text-gray-800">
                      {animal.couleur}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informations supplémentaires */}
          <div className="mt-6 grid grid-cols-1  gap-4">
            {animal.dateNaissance && (
              <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                <Calendar className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="text-sm text-yellow-600 font-medium">
                    Date de naissance
                  </p>
                  <p className="font-semibold text-gray-800">
                    {format(new Date(animal.dateNaissance), "dd MMMM yyyy", {
                      locale: fr,
                    })}
                  </p>
                </div>
              </div>
            )}

            {animal.laitiere !== undefined && (
              <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                <Heart className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm text-indigo-600 font-medium">
                    Laitière
                  </p>
                  <p className="font-semibold text-gray-800">
                    {animal.laitiere ? "Oui" : "Non"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section propriétaire */}
      {animal.proprietaire && (
        <div className="mt-8 p-6 bg-gray-50 rounded-xl">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Propriétaire
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Nom complet</p>
              <p className="font-semibold text-gray-800">
                {animal.proprietaire.prenom} {animal.proprietaire.nom}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Matricule</p>
              <p className="font-mono font-semibold text-gray-800">
                {animal.proprietaire.matricule}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date d'enrôlement</p>
              <p className="font-semibold text-gray-800">
                {animal.dateEnrolement
                  ? format(new Date(animal.dateEnrolement), "dd/MM/yyyy", {
                      locale: fr,
                    })
                  : format(new Date(), "dd/MM/yyyy", {
                      locale: fr,
                    })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
